// Preset due-date ranges for the tasks filter. Kept in its own module (not the page component)
// so the page file only exports a component (react-refresh) and this stays unit-testable.
import { datePartsInPinnedZone } from './pinnedZone';

const pad = (n: number) => String(n).padStart(2, '0');

// Civil-date arithmetic done in UTC, so the machine's own zone never enters it: Date.UTC normalises
// month/day overflow (day 0 = last day of the previous month), and the getUTC* reads undo it.
const civil = (year: number, month0: number, day: number) => new Date(Date.UTC(year, month0, day));
const iso = (d: Date) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;

/**
 * Map a preset due-range key to the inclusive {dueAfter, dueBefore} the API expects (yyyy-MM-dd,
 * '' = unbounded). 'custom' passes the manual pickers through unchanged; unknown keys → unbounded.
 *
 * 🔴 "Today" is today in America/New_York (fleet filter contract §9.3/§9.4, the interactive
 * context), NOT the browser's zone. Until 2026-10-20 this read `today.getFullYear()/getMonth()/
 * getDate()` — correct only because the staff laptops are set to Eastern time, and wrong on any
 * machine that is not (a UTC CI runner fails C28). The preset KEY is still what is stored and it is
 * still re-resolved on load (§9.4) — the zone was right in practice and is now right by construction.
 *
 * Bounds stay INCLUSIVE `dueBefore` (the API's contract). The fleet grammar's half-open `lt` is a
 * recorded divergence (§9.4 "bounds, not zones"), deliberately not changed here.
 */
export function computeDueRange(
  range: string, customFrom: string, customTo: string, now: Date = new Date(),
): { dueAfter: string; dueBefore: string } {
  const { year: y, month, day } = datePartsInPinnedZone(now);
  const m = month - 1;
  switch (range) {
    case 'overdue':   // due strictly before today → up to and including yesterday
      return { dueAfter: '', dueBefore: iso(civil(y, m, day - 1)) };
    case 'thisMonth':
      return { dueAfter: iso(civil(y, m, 1)), dueBefore: iso(civil(y, m + 1, 0)) };
    case 'next30':
      return { dueAfter: iso(civil(y, m, day)), dueBefore: iso(civil(y, m, day + 30)) };
    case 'thisQuarter': {
      const qs = Math.floor(m / 3) * 3;
      return { dueAfter: iso(civil(y, qs, 1)), dueBefore: iso(civil(y, qs + 3, 0)) };
    }
    case 'custom':
      return { dueAfter: customFrom, dueBefore: customTo };
    default:
      return { dueAfter: '', dueBefore: '' };
  }
}
